import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useEffect } from 'react';
import { differenceInYears, format } from 'date-fns';
import { CameraOffIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePatient } from '@hooks/usePatient.ts';
import { useJawsScanSummariesByPatient } from '@hooks/useJawsScan.ts';
import { usePatientDetailStore } from '@patientMenu/hooks/usePatientDetailStore';
import { PatientToolBar } from '@patientMenu/patientDetail/PatientToolBar.tsx';
import NotesPanel from '@patientMenu/patientDetail/patientScan/NotesPanel.tsx';
import TeethPreview from '@patientMenu/patientDetail/patientScan/TeethPreview.tsx';
import PatientScanList from './PatientScanList.tsx';
import type { Status as ScanStatus } from '@/schema/jawsScan';

export default function PatientDetail() {
  const navigate = useNavigate();
  const { patientId, scanId } = useParams<{ patientId: string; scanId?: string }>();
  useDocumentTitle(`Patients | detail ${patientId}`); {/*TODO fullName*/}
  const { selectedScan, setSelectedScan } = usePatientDetailStore();

  if (!patientId) {
    return null;
  }

  const patientQuery = usePatient(patientId);

  const jawsScanQuery = useJawsScanSummariesByPatient(patientId);

  useEffect(() => {
    setSelectedScan(null);
  }, [patientId, setSelectedScan]);

  useEffect(() => {
    const scans = jawsScanQuery.data;
    if (!scans) {
      return;
    }

    if (scanId) {
      const matchedScan = scans.find((scan) => scan.id === scanId);

      if (matchedScan) {
        if (selectedScan?.id !== matchedScan.id) {
          setSelectedScan(matchedScan);
        }
        return;
      }

      if (scans.length === 0) {
        if (selectedScan !== null) {
          setSelectedScan(null);
        }
        navigate(`/patients/${patientId}`, { replace: true });
        return;
      }

      navigate(`/patients/${patientId}/scans/${scans[0].id}`, { replace: true });
      return;
    }

    if (scans.length === 0) {
      if (selectedScan !== null) {
        setSelectedScan(null);
      }
      return;
    }

    const nextScan = scans.find((scan) => scan.id === selectedScan?.id) ?? scans[0];

    if (!selectedScan || selectedScan.id !== nextScan.id) {
      setSelectedScan(nextScan);
    }
  }, [scanId, jawsScanQuery.data, navigate, patientId, selectedScan, setSelectedScan]);

  if (patientQuery.isLoading || !patientQuery.data || jawsScanQuery.isLoading || !jawsScanQuery.data) {
    return (
      <div className={'flex justify-center items-center h-full'}>
        <p className={'text-white'}>Loading...</p>
      </div>
    );
  }

  const patient = patientQuery.data;
  const scansSummary = jawsScanQuery.data ?? [];

  const fullName = [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(' '); // TODO zustand?

  const age = patient.dateOfBirth ? differenceInYears(new Date(), new Date(patient.dateOfBirth)) : null;

  const sortedScans = [...scansSummary].sort((a, b) => new Date(b.scanDate).getTime() - new Date(a.scanDate).getTime());

  const totalScans = sortedScans.length;
  const lastScanDate = sortedScans[0] ? format(new Date(sortedScans[0].scanDate), 'MMM d, yyyy') : '—';

  const getStatusLabel = (status: ScanStatus | null | undefined) => {
    switch (status) {
      case 'Scanned':
        return 'Scanned';
      case 'Segmented':
        return 'Segmented';
      case 'Corrected':
        return 'Corrected';
      case 'Printing':
        return 'Printing';
      case 'Braces Created':
        return 'Braces created';
      case 'Printed':
        return 'Printed';
      case 'Empty':
        return 'Not started';
      default:
        return 'No scan selected';
    }
  };

  const activeScanStatus = selectedScan ? getStatusLabel(selectedScan.status) : 'No scan selected';
  const activeScanDate = selectedScan ? format(new Date(selectedScan.scanDate), 'MMM d, yyyy') : '—';

  const headerStats = [
    { label: 'Total scans', value: totalScans.toString() },
    { label: 'Latest scan', value: lastScanDate },
    { label: 'Current scan', value: activeScanDate },
    { label: 'Status', value: activeScanStatus },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 text-white">
      <div className="flex shrink-0 flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <h2 className="text-xl font-semibold tracking-tight text-white">{fullName}</h2>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/55">
            <span>Clinic: {patient.clinicName}</span>
            <span>Orthodontist: {patient.orthodontistName}</span>
            {age !== null && <span>Age: {age}</span>}
          </div>
        </div>
        <PatientToolBar />
      </div>

      <div className="grid shrink-0 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {headerStats.map(({ label, value }) => (
          <div
            key={label}
            className="surface-tile px-3 py-3 text-sm text-white/85"
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/45">{label}</p>
            <p className="mt-1 text-base font-semibold text-white">{value}</p>
          </div>
        ))}
      </div>

      {selectedScan === null ? (
        <div className="surface-tile flex flex-col items-center justify-center gap-3 border-dashed px-6 py-14 text-white/60">
          <CameraOffIcon className="h-12 w-12 text-white/30" />
          <div className="text-center">
            <p className="text-sm font-semibold text-white/85">No scans yet</p>
            <p className="mt-0.5 text-xs text-white/50">Import a scan to start working with this patient.</p>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex min-h-0 flex-[3] flex-col gap-3 lg:flex-row">
            <TeethPreview className="h-full min-h-0 w-full lg:min-w-0 lg:flex-[3]" />
            <PatientScanList className="h-full min-h-0 w-full lg:min-w-0 lg:flex-[2]" scansSummary={sortedScans} />
          </div>
          <NotesPanel className="min-h-[7rem] w-full flex-[2] shrink-0" />
        </div>
      )}
    </div>
  );
}
